"use client"
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Subject } from '@/types/subject'
import { Calendar, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import React, { useEffect, useState } from 'react'


function calculateTimeLeft(examDate: Date | string) {
  try {
    const now = new Date().getTime()
    const examTime = new Date(examDate).getTime()

    // 检查日期是否有效
    if (isNaN(examTime)) {
      console.error("无效的考试日期:", examDate)
      return null
    }

    const difference = examTime - now

    if (difference <= 0) {
      return null
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds }
  } catch (error) {
    console.error("计算倒计时时出错:", error)
    return null
  }
}

export default function SubjectTimedown({ subject }: { subject: Subject }) {
  const examInfo = subject.examInfo
  const [timeLeft, setTimeLeft] = useState<{
    days: number
    hours: number
    minutes: number
    seconds: number
  } | null>(null)
    // 倒计时逻辑
  useEffect(() => {
    if (!examInfo?.examDate) {
      setTimeLeft(null)
      return
    }

    const updateTimeLeft = () => {
      try {
        const timeLeft = calculateTimeLeft(examInfo.examDate!)
        setTimeLeft(timeLeft)
      } catch (error) {
        console.error("计算倒计时失败:", error)
        setTimeLeft(null)
      }
    }

    updateTimeLeft()
    const timer = setInterval(updateTimeLeft, 1000)

    return () => clearInterval(timer)
  }, [examInfo])
  
  return (
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <Calendar className="h-5 w-5" />
            考试倒计时
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {examInfo ? (
            <>
              <div className="text-center">
                <h3 className="font-semibold text-lg text-blue-800 dark:text-blue-200">
                  {examInfo.examName}
                </h3>
                {examInfo.examDate && (
                  <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
                    {new Date(examInfo.examDate).toLocaleDateString("zh-CN", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      weekday: "long",
                    })}
                  </p>
                )}
              </div>

              {timeLeft ? (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {timeLeft.days}
                    </div>
                    <div className="text-xs text-blue-500 dark:text-blue-300">天</div>
                  </div>
                  <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {timeLeft.hours}
                    </div>
                    <div className="text-xs text-blue-500 dark:text-blue-300">时</div>
                  </div>
                  <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {timeLeft.minutes}
                    </div>
                    <div className="text-xs text-blue-500 dark:text-blue-300">分</div>
                  </div>
                  <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {timeLeft.seconds}
                    </div>
                    <div className="text-xs text-blue-500 dark:text-blue-300">秒</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Badge
                    variant="secondary"
                    className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                  >
                    考试已结束
                  </Badge>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-300">
                <Clock className="h-4 w-4" />
                <span>{examInfo.examTime || "上午 9:00"} 开考</span>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">暂无考试安排</div>
            </div>
          )}
        </CardContent>
      </Card>
  )
}
